import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API, api, formatApiError } from "@/lib/api";
import { localDay } from "@/lib/writing";

// Debounced chapter autosave: queues per chapter, saves in order, retries while offline and flushes on leave
export const useAutosave = (onSaved) => {
  const pending = useRef({});
  const timer = useRef(null);
  const chain = useRef(Promise.resolve());
  const cb = useRef(onSaved);
  cb.current = onSaved;
  const [status, setStatus] = useState("saved");
  const flushRef = useRef(null);

  const run = useCallback(async () => {
    const jobs = Object.entries(pending.current);
    pending.current = {};
    for (const [cid, patch] of jobs) {
      try {
        const { data } = await api.put(`/writing/chapters/${cid}`, { ...patch, day: localDay() });
        cb.current(data);
      } catch (e) {
        if (e.response && e.response.status < 500) { toast.error(formatApiError(e)); continue; }
        pending.current[cid] = { ...patch, ...pending.current[cid] };
        setStatus("offline");
        clearTimeout(timer.current);
        timer.current = setTimeout(() => flushRef.current(), 4000);
        return;
      }
    }
    setStatus(Object.keys(pending.current).length ? "saving" : "saved");
  }, []);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    chain.current = chain.current.then(run);
    return chain.current;
  }, [run]);
  flushRef.current = flush;

  const queue = useCallback((cid, patch) => {
    pending.current[cid] = { ...pending.current[cid], ...patch };
    setStatus((s) => (s === "offline" ? s : "saving"));
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 1000);
  }, [flush]);

  useEffect(() => {
    const leave = () => {
      Object.entries(pending.current).forEach(([cid, patch]) => fetch(`${API}/writing/chapters/${cid}`, {
        method: "PUT", keepalive: true, credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...patch, day: localDay() }),
      }));
    };
    window.addEventListener("beforeunload", leave);
    window.addEventListener("online", flush);
    return () => { window.removeEventListener("beforeunload", leave); window.removeEventListener("online", flush); flush(); };
  }, [flush]);

  return { status, queue, flush };
};
